import React, { useState, useEffect } from 'react';
import {
  FiPlus, FiSearch, FiFilter, FiEdit2, FiTrash2,
  FiEye, FiDownload, FiUpload, FiPackage, FiAlertCircle
} from 'react-icons/fi';
import { productService } from '../services/api';
import { usePaginatedApi } from '../hooks/useApi';
import { Product } from '../models';

const ProductsEnhanced = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState(new Product());

  // Use paginated API hook for products
  const {
    data: products,
    loading,
    error,
    page,
    totalPages,
    total,
    limit,
    fetchData,
    nextPage,
    previousPage,
    goToPage,
    changeLimit,
    refresh
  } = usePaginatedApi(productService.getProducts, {
    category: selectedCategory !== 'all' ? selectedCategory : undefined
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const result = await productService.getCategories();
      if (result.success) {
        setCategories(result.data);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleSearch = () => {
    fetchData(1, {
      search: searchQuery,
      category: selectedCategory !== 'all' ? selectedCategory : undefined
    });
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    fetchData(1, {
      search: searchQuery,
      category: category !== 'all' ? category : undefined
    });
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData(new Product(product));
    setShowModal(true);
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      const result = await productService.deleteProduct(productId);
      if (result.success) {
        refresh();
      } else {
        alert(result.error || 'Failed to delete product');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let result;
      if (editingProduct) {
        result = await productService.updateProduct(editingProduct.id, formData);
      } else {
        result = await productService.createProduct(formData);
      }

      if (result.success) {
        setShowModal(false);
        setEditingProduct(null);
        setFormData(new Product());
        refresh();
      } else {
        alert(result.error || 'Operation failed');
      }
    } catch (error) {
      alert('An error occurred. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const calculateTotalPrice = () => {
    const price = parseFloat(formData.price) || 0;
    const makingCharges = parseFloat(formData.makingCharges) || 0;
    const gst = parseFloat(formData.gst) || 0;
    return (price + makingCharges + (price * gst / 100)).toFixed(2);
  };

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      totalPrice: calculateTotalPrice()
    }));
  }, [formData.price, formData.makingCharges, formData.gst]);

  if (error && !products.length) {
    return (
      <div className="container-fluid p-0">
        <div className="alert alert-warning d-flex align-items-center" role="alert">
          <FiAlertCircle className="me-2" />
          <span>{error}</span>
          <button onClick={refresh} className="btn btn-sm btn-warning ms-auto">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="fw-bold mb-1">Product Catalog</h2>
          <p className="text-muted small mb-0">Manage your gold jewelry inventory</p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn glass-card border-0 text-white px-3">
            <FiDownload /> Export
          </button>
          <button className="btn glass-card border-0 text-white px-3">
            <FiUpload /> Import
          </button>
          <button
            className="btn btn-primary px-3"
            onClick={() => {
              setEditingProduct(null);
              setFormData(new Product());
              setShowModal(true);
            }}
          >
            <FiPlus /> Add Product
          </button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="glass-card p-3 mb-4">
        <div className="row g-3">
          <div className="col-md-5">
            <div className="input-group">
              <span className="input-group-text bg-transparent border-end-0">
                <FiSearch />
              </span>
              <input
                type="text"
                className="form-control border-start-0 ps-0"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={selectedCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              <option value="all">All Categories</option>
              <option value="gold">Gold</option>
              <option value="silver">Silver</option>
              <option value="diamond">Diamond</option>
              <option value="platinum">Platinum</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <select
              className="form-select"
              value={limit}
              onChange={(e) => changeLimit(parseInt(e.target.value))}
            >
              <option value="10">10 per page</option>
              <option value="20">20 per page</option>
              <option value="50">50 per page</option>
              <option value="100">100 per page</option>
            </select>
          </div>
          <div className="col-md-2">
            <button onClick={handleSearch} className="btn btn-primary w-100">
              <FiFilter /> Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="glass-card p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0">Products ({total})</h5>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted mt-3">Loading products...</p>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-dark table-hover">
                <thead>
                  <tr className="text-muted small uppercase">
                    <th>SKU</th>
                    <th>Product Name</th>
                    <th>Category</th>
                    <th>Weight</th>
                    <th>Purity</th>
                    <th>Price</th>
                    <th>Stock</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.id} className="align-middle">
                      <td className="fw-bold">{product.sku}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <FiPackage />
                          <span>{product.name}</span>
                        </div>
                      </td>
                      <td>{product.category}</td>
                      <td>{product.weight}g</td>
                      <td>{product.purity}</td>
                      <td className="fw-bold">₹{product.totalPrice?.toLocaleString()}</td>
                      <td>
                        <span className={`badge bg-${
                          product.stockQuantity > product.minStockLevel ? 'success' :
                          product.stockQuantity > 0 ? 'warning' : 'danger'
                        } bg-opacity-10 text-${
                          product.stockQuantity > product.minStockLevel ? 'success' :
                          product.stockQuantity > 0 ? 'warning' : 'danger'
                        }`}>
                          {product.stockQuantity} units
                        </span>
                      </td>
                      <td>
                        <span className={`badge bg-${
                          product.stockQuantity > 0 ? 'success' : 'danger'
                        } bg-opacity-10 text-${
                          product.stockQuantity > 0 ? 'success' : 'danger'
                        }`}>
                          {product.stockQuantity > 0 ? 'In Stock' : 'Out of Stock'}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm text-info"
                            title="View"
                            onClick={() => handleEdit(product)}
                          >
                            <FiEye />
                          </button>
                          <button
                            className="btn btn-sm text-warning"
                            title="Edit"
                            onClick={() => handleEdit(product)}
                          >
                            <FiEdit2 />
                          </button>
                          <button
                            className="btn btn-sm text-danger"
                            title="Delete"
                            onClick={() => handleDelete(product.id)}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-4">
                <div className="text-muted small">
                  Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} products
                </div>
                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                      <button className="page-link bg-transparent border-0" onClick={previousPage}>
                        Previous
                      </button>
                    </li>
                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                      const pageNum = i + 1;
                      return (
                        <li key={pageNum} className={`page-item ${page === pageNum ? 'active' : ''}`}>
                          <button
                            className="page-link bg-transparent border-0"
                            onClick={() => goToPage(pageNum)}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    })}
                    <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link bg-transparent border-0" onClick={nextPage}>
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </>
        )}
      </div>

      {/* Product Modal */}
      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content glass-card">
              <div className="modal-header border-0">
                <h5 className="modal-title">
                  {editingProduct ? 'Edit Product' : 'Add New Product'}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label text-muted small fw-bold">Product Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-muted small fw-bold">SKU</label>
                      <input
                        type="text"
                        className="form-control"
                        name="sku"
                        value={formData.sku}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-bold">Category</label>
                      <select
                        className="form-select"
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Category</option>
                        <option value="gold">Gold</option>
                        <option value="silver">Silver</option>
                        <option value="diamond">Diamond</option>
                        <option value="platinum">Platinum</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-bold">Sub Category</label>
                      <select
                        className="form-select"
                        name="subCategory"
                        value={formData.subCategory}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Sub Category</option>
                        <option value="necklace">Necklace</option>
                        <option value="ring">Ring</option>
                        <option value="bracelet">Bracelet</option>
                        <option value="earrings">Earrings</option>
                        <option value="pendant">Pendant</option>
                        <option value="chain">Chain</option>
                        <option value="bangle">Bangle</option>
                        <option value="anklet">Anklet</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-bold">Purity</label>
                      <select
                        className="form-select"
                        name="purity"
                        value={formData.purity}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Purity</option>
                        <option value="24K">24K</option>
                        <option value="22K">22K</option>
                        <option value="18K">18K</option>
                        <option value="14K">14K</option>
                        <option value="925">Silver 925</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-bold">Weight (grams)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        name="weight"
                        value={formData.weight}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-bold">Price (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        name="price"
                        value={formData.price}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-bold">Making Charges (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        name="makingCharges"
                        value={formData.makingCharges}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-bold">GST (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        name="gst"
                        value={formData.gst}
                        onChange={handleInputChange}
                        placeholder="3"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-bold">Total Price (₹)</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.totalPrice}
                        disabled
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-bold">Stock Quantity</label>
                      <input
                        type="number"
                        className="form-control"
                        name="stockQuantity"
                        value={formData.stockQuantity}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-muted small fw-bold">Barcode</label>
                      <input
                        type="text"
                        className="form-control"
                        name="barcode"
                        value={formData.barcode}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-muted small fw-bold">Min Stock Level</label>
                      <input
                        type="number"
                        className="form-control"
                        name="minStockLevel"
                        value={formData.minStockLevel}
                        onChange={handleInputChange}
                        placeholder="5"
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label text-muted small fw-bold">Description</label>
                      <textarea
                        className="form-control"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows="3"
                      ></textarea>
                    </div>
                    <div className="col-12">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          name="hallmarkCertified"
                          checked={formData.hallmarkCertified}
                          onChange={handleInputChange}
                          id="hallmarkCheck"
                        />
                        <label className="form-check-label" htmlFor="hallmarkCheck">
                          Hallmark Certified
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer border-0 mt-4">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      {editingProduct ? 'Update Product' : 'Add Product'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsEnhanced;